// VersionTimeline — vertical list of versions with collapsible diffs.
//
// Pure renderer — receives versions + status meta + click handlers.
// Doesn't fetch, doesn't gate, doesn't decide.
import React, { useState } from 'react';
import {
  Clock, GitBranch, ChevronDown, ChevronRight,
  CheckCircle2, Pencil, Archive, Send,
} from 'lucide-react';
import { ActionGatedButton } from '../ActionGate';
import VersionDiffPanel from './VersionDiffPanel';

const STATUS_META = {
  draft:      { label: 'مسوّدة',  bg: 'bg-amber-100',   fg: 'text-amber-800',   icon: Pencil },
  posted:     { label: 'معتمد',   bg: 'bg-emerald-100', fg: 'text-emerald-800', icon: CheckCircle2 },
  superseded: { label: 'مستبدَل', bg: 'bg-slate-200',   fg: 'text-slate-600',   icon: Archive },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('ar-SA', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function VersionTimeline({
  versions,
  config,
  onPost,
  testIdPrefix = 'version',
}) {
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  if (!versions || versions.length === 0) {
    return <div className="text-sm text-slate-400 text-center py-6">لا توجد إصدارات</div>;
  }

  return (
    <ol className="relative border-r-2 border-slate-200 pr-4 space-y-3">
      {versions.map((v) => {
        const meta = STATUS_META[v.status] || STATUS_META.draft;
        const Icon = meta.icon;
        const key = v.id || `v${v.version_number}`;
        const isExpanded = !!expanded[key];
        return (
          <li
            key={key}
            className="relative pr-4"
            data-testid={`${testIdPrefix}-row-v${v.version_number}`}
          >
            <div className="absolute right-[-9px] top-2 w-4 h-4 rounded-full bg-white border-2 border-slate-300 flex items-center justify-center">
              <GitBranch size={8} className="text-slate-500" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => toggle(key)}
                className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900"
              >
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <span className="font-semibold">v{v.version_number}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${meta.bg} ${meta.fg} flex items-center gap-1`}>
                  <Icon size={10} />
                  {meta.label}
                </span>
                {v.reason && (
                  <span className="text-xs text-slate-500 truncate max-w-[200px]">— {v.reason}</span>
                )}
              </button>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Clock size={11} />
                  {fmtDate(v.posted_at || v.created_at)}
                </span>
                {v.status === 'draft' && (
                  <ActionGatedButton
                    action={config.actionAmendPost}
                    onClick={() => onPost(v.id)}
                    data-testid={`${testIdPrefix}-post-amendment-${v.id}`}
                    className="px-2 py-1 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Send size={11} />
                    ترحيل
                  </ActionGatedButton>
                )}
              </div>
            </div>
            {isExpanded && (
              <VersionDiffPanel
                changeset={v.changeset}
                config={config}
                testIdPrefix={`${testIdPrefix}-diff`}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
